// ============================================
// 🏠 APP.JS - Componente Principal (Navegação)
// ============================================
// ✅ PRÉ-CARREGA DADOS ANTES DE RENDERIZAR
// ✅ SIDEBAR com h-screen — rodapé sempre visível
// ✅ Sub-abas de Disparo e Financeiro no sidebar
// ✅ Ordem atualizada do menu
// ✅ Renomeado para IntegrAI
// ✅ MENU HAMBÚRGUER MOBILE RESPONSIVO
// ✅ SISTEMA DE AUTENTICAÇÃO E PERMISSÕES
// ============================================
import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import {
  Target, MessageSquare, Upload, TrendingUp,
  Phone, LayoutGrid, Edit2, BarChart2,
  DollarSign, ShoppingCart, CreditCard, FileText,
  Package, UserCog, LayoutDashboard, Sparkles,
  ArrowDownCircle, ArrowUpCircle, BarChart3,
  Building2, Tag, Landmark, Boxes, CalendarClock, Wifi, Truck, Repeat, PieChart,
} from 'lucide-react';
import { logLogout } from './utils/auditoria';
import supabase from './services/supabaseClient';
import { getTenantById } from './services/tenantService';
// MULTI-TENANT: aplica tema (CSS vars) baseado no tenant ativo
import { useTenantTheme } from './hooks/useTenantTheme';
// MULTI-TENANT: contexto de tenant
import { useTenant } from './contexts/TenantContext';
// API & Config
import { carregarClientes } from './services/api';
import { resolverModulos, resolverProduto } from './config/planos';
// Store global (Zustand)
import useAppStore from './store/useAppStore';
// Autenticação & Permissões
import LoginPage from './components/Auth/LoginPage';
import ResetPasswordPage from './components/Auth/ResetPasswordPage';
import { podeAcessarAba } from './utils/permissoes';
// Componentes sempre carregados (visíveis em toda sessão)
import AgenteSuporteChat from './components/Suporte/AgenteSuporteChat';
import ModalBoasVindas from './components/shared/ModalBoasVindas';
// Layouts multi-marca (novos clientes via tráfego pago)
import LayoutOrganizAI from './layouts/LayoutOrganizAI';
import LayoutIntegrAI from './layouts/LayoutIntegrAI';
import AppLayout from './layouts/AppLayout';
import TelaCarregando from './components/shared/TelaCarregando';
import { Toaster } from 'sonner';
// Landing pages de marketing (preview via ?preview=landing)
const LandingPreview = lazy(() => import('./components/Landing/LandingPreview'));

// MULTI-TENANT: página super admin (subdomínio "admin")
const SuperAdminPage = lazy(() => import('./components/SuperAdmin/SuperAdminPage'));

// Componentes lazy — carregados apenas quando a aba é acessada
const DashboardTab      = lazy(() => import('./components/Dashboard/DashboardTab'));
const AtendimentoTab    = lazy(() => import('./components/AtendimentoIA/AtendimentoTab'));
const CRMRecompraTab    = lazy(() => import('./components/CRMRecompra/CRMRecompraTab'));
const ConfigDisparoTab  = lazy(() => import('./components/ConfigDisparo/ConfigDisparoTab'));
const FinanceiroTab     = lazy(() => import('./components/Financeiro/FinanceiroTab'));
const EstoqueTab        = lazy(() => import('./components/Estoque/EstoqueTab'));
const GerenciarUsuarios  = lazy(() => import('./components/Admin/GerenciarUsuarios'));
const LogsAuditoria      = lazy(() => import('./components/Admin/LogsAuditoria'));
const GerenciarInstancias = lazy(() => import('./components/Admin/GerenciarInstancias'));
const ConfigIATab       = lazy(() => import('./components/ConfigIA/ConfigIATab'));
const ConfiguracoesTab  = lazy(() => import('./components/Configuracoes/ConfiguracoesTab'));
const RelatoriosTab     = lazy(() => import('./components/Relatorios/RelatoriosTab'));
const WhatsAppTab       = lazy(() => import('./components/WhatsApp/WhatsAppTab'));
const CadastroProdutosTab = lazy(() => import('./components/Financeira/CadastroProdutosTab'));
const TemplatesMetaTab    = lazy(() => import('./components/TemplatesMeta/TemplatesMetaTab'));
const MetricasMetaTab     = lazy(() => import('./components/MetricasMeta/MetricasMetaTab'));
const EntregasTab         = lazy(() => import('./components/Entregas/EntregasTab'));
const RastreioPage        = lazy(() => import('./components/Rastreio/RastreioPage'));
const AnalisesTab         = lazy(() => import('./components/Analises/AnalisesTab'));

// SUB_ABAS do Financeiro definida aqui para não quebrar o lazy loading do FinanceiroTab
// (mantida em sincronia com FinanceiroTab.js)
const subAbasFinanceiro = [
  { id: 'dashboard',       label: 'Dashboard',      icon: TrendingUp },
  { id: 'contas_pagar',    label: 'A Pagar',         icon: ArrowDownCircle },
  { id: 'contas_receber',  label: 'A Receber',       icon: ArrowUpCircle },
  { id: 'fluxo_caixa',     label: 'Caixa',           icon: BarChart3 },
  { id: 'fornecedores',    label: 'Fornecedores',    icon: Building2 },
  { id: 'categorias',      label: 'Categorias',      icon: Tag },
  { id: 'contas_bancarias',label: 'Contas',          icon: Landmark },
  { id: 'despesas',        label: 'Despesas',        icon: CreditCard },
  { id: 'dre',             label: 'DRE',             icon: FileText },
];


// ============================================
// 📋 ABAS PRINCIPAIS
// ============================================
const ABAS = [
  { id: 'atendimento',   label: 'Atendimento IA',         Icon: MessageSquare, badge: true },
  { id: 'vendas',        label: 'Vendas',                 Icon: ShoppingCart },
  { id: 'financeiro',    label: 'Financeiro',             Icon: DollarSign, temSubAbas: true },
  { id: 'estoque',       label: 'Estoque',                Icon: Boxes },
  { id: 'disparo',       label: 'Disparo de Mensagens',   Icon: Target, temSubAbas: true },
  { id: 'dashboard',     label: 'Dashboard',              Icon: LayoutDashboard },
  { id: 'cadastros',     label: 'Cadastro',               Icon: Package },
  { id: 'config-ia',     label: 'Configuração IA',        Icon: Sparkles },
  { id: 'crm-recompra',  label: 'CRM Recompra',           Icon: Repeat },
  { id: 'usuarios',      label: 'Gerenciar Usuários',     Icon: UserCog },
  { id: 'logs',          label: 'Logs de Auditoria',      Icon: FileText },
  { id: 'configuracoes', label: 'Importar Dados',         Icon: Upload },
  { id: 'relatorios',    label: 'Relatórios',             Icon: TrendingUp },
  { id: 'whatsapp',      label: 'Conectar WhatsApp',      Icon: Phone },
  { id: 'instancias',    label: 'Instâncias WhatsApp',    Icon: Wifi },
  { id: 'cadastro-produtos', label: 'Cadastro de Produtos', Icon: CreditCard },
  { id: 'templates-meta',   label: 'Templates Meta',       Icon: MessageSquare },
  { id: 'metricas-meta',   label: 'Métricas Meta',        Icon: BarChart2     },
  { id: 'entregas',        label: 'Entregas',             Icon: Truck         },
  { id: 'analises',        label: 'Análises',             Icon: PieChart      },
];

// ============================================
// 📋 SUB-ABAS DO DISPARO
// ============================================
const SUB_ABAS_DISPARO = [
  { id: 'grupo',         label: 'Por Grupo',              Icon: LayoutGrid    },
  { id: 'personalizado', label: 'Personalizado',          Icon: Edit2         },
  { id: 'progresso',     label: 'Acompanhamento',         Icon: BarChart2     },
  { id: 'agendamento',   label: 'Agendamento',            Icon: CalendarClock },
];

// ============================================
// 📋 SUB-ABAS DO FINANCEIRO (vindas do FinanceiroTab)
// ============================================
const SUB_ABAS_FINANCEIRO = subAbasFinanceiro.map(s => ({ ...s, Icon: s.icon }));


// ============================================
// 🔑 CONSTANTES DO TENANT Sou+Crédito
// ============================================
const SOU_MAIS_CREDITO_ID = '1a369d82-57c9-42f5-8d8d-f94fc663bec3';
const MODULOS_SOU_MAIS_CREDITO = [
  'atendimento', 'disparo', 'financeiro',
  'config-ia', 'configuracoes', 'relatorios', 'whatsapp',
  'cadastro-produtos',
  'usuarios', 'logs',
];

// ============================================
// 🗺️ HELPERS — extraídos para reduzir a
// complexidade ciclomática de CRMDashboard
// ============================================

const SPINNER_INDIGO = (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
  </div>
);

function renderAbaLazyIndigo(Component, props) {
  return <Suspense fallback={SPINNER_INDIGO}><Component {...props} /></Suspense>;
}

function criarMapaAbas({ setTotalNaoLidas, clientesCache, subAbaDisparo, setSubAbaDisparo, subAbaFinanceiro, setSubAbaFinanceiro, usuarioLogado }) {
  const financeiroBase = { onSubAbaChange: setSubAbaFinanceiro, EstoqueTab };
  const lazyProps      = { usuarioLogado };
  return {
    'dashboard':         () => <DashboardTab />,
    'atendimento':       () => <AtendimentoTab onNotificacoesUpdate={setTotalNaoLidas} />,
    'crm-recompra':      () => <CRMRecompraTab />,
    'disparo':           () => <ConfigDisparoTab subAbaAtiva={subAbaDisparo} onSubAbaChange={setSubAbaDisparo} />,
    'financeiro':        () => <FinanceiroTab subAbaAtiva={subAbaFinanceiro} {...financeiroBase} />,
    'vendas':            () => <FinanceiroTab subAbaAtiva="vendas"    {...financeiroBase} />,
    'estoque':           () => <FinanceiroTab subAbaAtiva="estoque"   {...financeiroBase} />,
    'analises':          () => <Suspense fallback={SPINNER_INDIGO}><AnalisesTab /></Suspense>,
    'cadastros':         () => <FinanceiroTab subAbaAtiva="cadastros" {...financeiroBase} />,
    'config-ia':         () => <ConfigIATab />,
    'usuarios':          () => <GerenciarUsuarios />,
    'instancias':        () => <GerenciarInstancias />,
    'logs':              () => <LogsAuditoria />,
    'configuracoes':     () => <ConfiguracoesTab />,
    'relatorios':        () => <RelatoriosTab />,
    'whatsapp':          () => <WhatsAppTab />,
    'cadastro-produtos': () => renderAbaLazyIndigo(CadastroProdutosTab, lazyProps),
    'templates-meta':    () => <TemplatesMetaTab />,
    'metricas-meta':     () => <Suspense fallback={SPINNER_INDIGO}><MetricasMetaTab /></Suspense>,
    'entregas':          () => <Suspense fallback={SPINNER_INDIGO}><EntregasTab /></Suspense>,
  };
}

function renderAbaAtiva(abaAtiva, ctx) {
  const render = criarMapaAbas(ctx)[abaAtiva];
  return render ? render() : null;
}

function filtrarAbasVisiveis(modulosResolvidos, tenant) {
  // 'crm-recompra' e 'config-ia-beta' são opt-in por tenant: entram apenas
  // quando listados em tenants.modulos, como qualquer outra aba
  const baseAbas = modulosResolvidos
    ? ABAS.filter(a => modulosResolvidos.includes(a.id))
    : ABAS.filter(a => a.id !== 'crm-recompra' && a.id !== 'fiscal');
  return baseAbas;
}

function calcularModulosOrdenados(tenant) {
  const produto = resolverProduto(tenant);
  const base = produto
    ? produto.modulos
    : tenant?.id === SOU_MAIS_CREDITO_ID
    ? MODULOS_SOU_MAIS_CREDITO
    // 'crm-recompra' e 'fiscal' são opt-in por tenant: nunca entram pelo fallback "todas as abas"
    : (tenant?.modulos || ABAS.filter(a => a.id !== 'crm-recompra' && a.id !== 'fiscal').map(a => a.id));
  // Empresa de produto: a lista já vem fechada, sem as abas extras do Financeiro
  const extras = produto ? [] : ['vendas', 'estoque', 'cadastros', 'analises']
    .filter(m => base.includes('financeiro') && !base.includes(m));
  const comExtras = extras.length ? [...base, ...extras] : base;
  const ordem = ABAS.map(a => a.id);
  return [...comExtras].sort((a, b) => {
    const ai = ordem.indexOf(a);
    const bi = ordem.indexOf(b);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

// Rotas públicas — sem login. Retorna o elemento a renderizar ou null.
function renderRotaPublica() {
  // Páginas estáticas (Política de Privacidade / Termos de Serviço).
  // Em produção a Vercel serve os .html direto via rewrite; este fallback
  // cobre o dev local, onde o dev server entrega a SPA para qualquer path.
  const pathLegal = ['/politica-de-privacidade', '/termos-de-servico']
    .find(p => window.location.pathname === p || window.location.pathname === `${p}/`);
  if (pathLegal) {
    window.location.replace(`${pathLegal}.html`);
    return <div className="min-h-screen bg-slate-50" />;
  }

  // Rota pública de rastreio de entrega
  if (window.location.pathname.startsWith('/rastreio/')) {
    const token = window.location.pathname.split('/rastreio/')[1];
    return (
      <Suspense fallback={<div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" /></div>}>
        <RastreioPage token={token} />
      </Suspense>
    );
  }

  return null;
}

function extrairErroLogin() {
  const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
  const erroHash = hashParams.get('error_code');
  if (erroHash === 'otp_expired') return 'O link de redefinição de senha expirou. Solicite um novo abaixo.';
  if (erroHash) return 'Link inválido. Solicite um novo email de redefinição.';
  return null;
}

// ============================================
// 🎯 COMPONENTE PRINCIPAL
// ============================================
const CRMDashboard = () => {
  // MULTI-TENANT: tenant carregado após login; modulos controla abas visíveis
  const { isSuperAdmin, tenant, setTenantData } = useTenant();

  // MULTI-TENANT: aplica CSS variables (--tenant-cor-primaria, etc.) e título da aba
  useTenantTheme();

  // ─── Zustand store (estado global compartilhado) ─────────
  // NOTA: todos os hooks devem ser declarados antes de qualquer return condicional
  const {
    setUsuario:          setStoreUsuario,
    clearUsuario:        clearStoreUsuario,
    setClientesCache:    setStoreClientesCache,
    setDadosCarregados:  setStoreDadosCarregados,
    setTotalNaoLidas:    setStoreTotalNaoLidas,
    setAtendenteAtivo:   setStoreAtendenteAtivo,
    setTenant:           setStoreTenant,
    clearTenant:         clearStoreTenant,
  } = useAppStore();

  // Estados de autenticação
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [carregandoAuth, setCarregandoAuth] = useState(true);
  const [resetandoSenha, setResetandoSenha] = useState(() =>
    window.location.hash.includes('type=recovery')
  );

  // Estados da aplicação
  const [abaAtiva, setAbaAtiva] = useState('dashboard');
  const [subAbaDisparo, setSubAbaDisparo] = useState('grupo');
  const [subAbaFinanceiro, setSubAbaFinanceiro] = useState('dashboard');
  const [totalNaoLidas, setTotalNaoLidas] = useState(0);
  const [dadosCarregados, setDadosCarregados] = useState(false);
  const [clientesCache, setClientesCache] = useState([]);
  const [sidebarAberto, setSidebarAberto] = useState(false);
  const [, setMenuMobileAberto] = useState(false);
  const [mostrarBoasVindas, setMostrarBoasVindas] = useState(false);
  const [expandidoDisparo, setExpandidoDisparo] = useState(false);
  const [expandidoFinanceiro, setExpandidoFinanceiro] = useState(false);

  const [atendenteAtivo, setAtendenteAtivo] = useState(false);
  const [atendenteProcessando, setAtendenteProcessando] = useState(false);

  // ============================================
  // 🔐 VERIFICA AUTENTICAÇÃO AO CARREGAR (FASE 3)
  // ============================================
  useEffect(() => {
    // Tenta restaurar sessão existente (page reload, nova aba)

    const isPasswordRecovery = () =>
      window.location.hash.includes('type=recovery');

    // Caminho rápido: Zustand já tem cache — renderiza imediatamente e atualiza em background
    const restaurarComCache = (storeState) => {
      if (storeState.tenant) setTenantData(storeState.tenant);
      setUsuarioLogado(storeState.usuario);
      setCarregandoAuth(false);
      getTenantById(storeState.tenantId).then(freshTenant => {
        if (freshTenant) {
          setStoreTenant(freshTenant);
          setTenantData(freshTenant);
        }
      });
    };

    // Multi-empresa: respeita o último tenant escolhido; single-empresa: primeiro perfil
    const selecionarPerfil = (perfis, freshStore) => {
      if (freshStore.tenantId && perfis.length > 1) {
        return perfis.find(p => p.tenant_id === freshStore.tenantId) || perfis[0];
      }
      return perfis[0];
    };

    const buscarEAplicarTenant = async (u) => {
      const tenant = u.tenant_id ? await getTenantById(u.tenant_id) : null;
      setStoreUsuario(u);
      setStoreTenant(tenant);
      setTenantData(tenant);
      setUsuarioLogado(u);
    };

    // Zustand vazio mas sessão válida → busca perfil via RPC
    const restaurarSemCache = async () => {
      try {
        const { data: perfis } = await supabase.rpc('buscar_perfil_usuario');
        if (!perfis || perfis.length === 0) return;

        // Re-lê o Zustand após as chamadas async (pode ter hidratado enquanto esperávamos)
        const freshStore = useAppStore.getState();
        const u = selecionarPerfil(perfis, freshStore);

        if (freshStore.usuario && freshStore.tenantId && freshStore.tenant) {
          setTenantData(freshStore.tenant);
          setUsuarioLogado(freshStore.usuario);
        } else {
          await buscarEAplicarTenant(u);
        }
      } catch (err) {
        console.error('Erro ao restaurar sessão:', err);
      }
    };

    const restaurarSessao = async () => {
      if (isPasswordRecovery()) {
        setResetandoSenha(true);
        setCarregandoAuth(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        const storeState = useAppStore.getState();
        if (storeState.usuario && storeState.tenantId) {
          restaurarComCache(storeState);
          return;
        }
        await restaurarSemCache();
      }

      setCarregandoAuth(false);
    };

    restaurarSessao();

    // Observa eventos de auth: logout forçado e recuperação de senha
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setUsuarioLogado(null);
        setResetandoSenha(false);
      }
      if (event === 'PASSWORD_RECOVERY') {
        setResetandoSenha(true);
      }
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // 🔐 HANDLERS DE AUTENTICAÇÃO
  // ============================================
  const handleLogin = (usuario) => {
    setUsuarioLogado(usuario);
    const storeState = useAppStore.getState();
    const modulosLogin = resolverModulos(storeState.tenant);
    const primeiroModulo = (modulosLogin || []).find(m => podeAcessarAba(usuario, m)) || modulosLogin?.[0] || 'dashboard';
    setAbaAtiva(primeiroModulo);
  };

  const handleLogout = async () => {
    if (window.confirm('Deseja realmente sair?')) {
      await logLogout();
      await supabase.auth.signOut(); // invalida o JWT no servidor
      clearStoreUsuario();
      clearStoreTenant();
      setUsuarioLogado(null);
      setAbaAtiva('atendimento');
    }
  };

  // ============================================
  // ⚡ BUSCA STATUS DO ATENDENTE
  // ============================================
  useEffect(() => {
    if (!usuarioLogado) return;

    const buscarStatus = async () => {
      try {
        const tenantId = tenant?.id;
        if (!tenantId) return;
        const { data, error } = await supabase
          .from('config_atendente')
          .select('atendente_virtual')
          .eq('tenant_id', tenantId)
          .maybeSingle();
        if (!error && data) {
          setAtendenteAtivo(data.atendente_virtual);
        }
      } catch (err) {
        console.error('Erro ao buscar status do atendente:', err);
      }
    };
    buscarStatus();
  }, [usuarioLogado, tenant?.id]);

  // ============================================
  // 🔄 TOGGLE ATENDENTE
  // ============================================
  const handleToggleAtendente = useCallback(async () => {
    setAtendenteProcessando(true);
    try {
      const novoStatus = !atendenteAtivo;
      const tenantId = tenant?.id;
      if (!tenantId) return;
      const { error } = await supabase
        .from('config_atendente')
        .upsert(
          { tenant_id: tenantId, atendente_virtual: novoStatus, updated_at: new Date().toISOString() },
          { onConflict: 'tenant_id' }
        );
      if (!error) {
        setAtendenteAtivo(novoStatus);
      } else {
        console.error('Erro ao alternar atendente:', error);
      }
    } catch (err) {
      console.error('Erro ao alternar atendente:', err);
    } finally {
      setAtendenteProcessando(false);
    }
  }, [atendenteAtivo, tenant?.id]);

  // ============================================
  // 📊 PRÉ-CARREGA CLIENTES
  // Roda ao logar E ao volcar para esta aba se
  // cache ainda estiver vazio (resiliência)
  // ============================================
  useEffect(() => {
    // Aguarda tanto o usuário quanto o tenant estarem definidos
    // antes de buscar clientes — evita busca com tenant_id=null
    if (!usuarioLogado || !tenant?.id) return;

    // Limpa cache da empresa anterior para não mostrar dados stale
    setClientesCache([]);
    setDadosCarregados(false);

    const carregarDados = async () => {
      try {
        const clientes = await carregarClientes();
        if (clientes && clientes.length > 0) {
          setClientesCache(clientes);
        }
        setDadosCarregados(true);
      } catch (err) {
        console.error('Erro ao carregar clientes:', err);
        setDadosCarregados(true);
      }
    };
    carregarDados();
  }, [usuarioLogado, tenant?.id]); // tenant.id garante reload ao trocar de empresa

  // ── Retry automático se cache ficou vazio após login ──────
  useEffect(() => {
    if (!usuarioLogado || !dadosCarregados || clientesCache.length > 0) return;

    // dadosCarregados=true mas cache vazio → a busca anterior falhou
    // Aguarda 2s e tenta novamente (silenciosamente)
    const timer = setTimeout(async () => {
      try {
        const clientes = await carregarClientes();
        if (clientes && clientes.length > 0) {
          setClientesCache(clientes);
        }
      } catch {
        // ignora — não bloqueia a UI
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [usuarioLogado, dadosCarregados, clientesCache.length]);

  // ============================================
  // 🔄 SINCRONIZA STATE LOCAL → ZUSTAND STORE
  // Permite que componentes como Dashboard leiam
  // dados globais sem prop-drilling
  // ============================================
  useEffect(() => { setStoreUsuario(usuarioLogado); },        [usuarioLogado,    setStoreUsuario]);
  useEffect(() => { setStoreClientesCache(clientesCache); },  [clientesCache,    setStoreClientesCache]);
  useEffect(() => { setStoreDadosCarregados(dadosCarregados);}, [dadosCarregados, setStoreDadosCarregados]);
  useEffect(() => { setStoreTotalNaoLidas(totalNaoLidas); },  [totalNaoLidas,    setStoreTotalNaoLidas]);
  useEffect(() => { setStoreAtendenteAtivo(atendenteAtivo); },[atendenteAtivo,   setStoreAtendenteAtivo]);

  // Mostra popup de regras do jogo apenas para Sou+Crédito, uma vez por sessão
  useEffect(() => {
    if (!usuarioLogado || !tenant?.id) return;
    if (tenant.id !== '1a369d82-57c9-42f5-8d8d-f94fc663bec3') return;
    const jaViu = sessionStorage.getItem('boasVindas_jogo');
    if (!jaViu) {
      setMostrarBoasVindas(true);
      sessionStorage.setItem('boasVindas_jogo', '1');
    }
  }, [usuarioLogado, tenant?.id]);

  // ============================================
  // 📋 ABAS FILTRADAS POR MÓDULOS DO TENANT
  // Precedência: produto (tenant.plano) > tenant.modulos (banco) > todas
  // ============================================
  const modulosResolvidos = resolverModulos(tenant);
  const abasVisiveis = filtrarAbasVisiveis(modulosResolvidos, tenant);

  // Corrige abaAtiva se o módulo atual não existir no plano (ex: downgrade ou plano sem dashboard)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!usuarioLogado || !abasVisiveis.length) return;
    const abaAtual = abasVisiveis.find(a => a.id === abaAtiva);
    if (!abaAtual || !podeAcessarAba(usuarioLogado, abaAtiva)) {
      const primeiraAcessivel = abasVisiveis.find(a => podeAcessarAba(usuarioLogado, a.id));
      setAbaAtiva(primeiraAcessivel?.id || abasVisiveis[0].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.plano, tenant?.marca, tenant?.id, usuarioLogado]);

  // ============================================
  // 🔒 HANDLER TROCA DE ABA COM VERIFICAÇÃO
  // ============================================
  const handleTrocarAba = (abaId) => {
    if (!podeAcessarAba(usuarioLogado, abaId)) {
      alert('Você não tem permissão para acessar esta área');
      return;
    }

    // Toggle das sub-abas ao clicar na aba já ativa
    if (abaId === 'disparo') {
      if (abaAtiva === 'disparo') {
        // Se sidebar fechado, abre; se aberto, faz toggle
        if (!sidebarAberto) {
          setSidebarAberto(true);
          setExpandidoDisparo(true);
        } else {
          setExpandidoDisparo(prev => !prev);
        }
        return;
      }
      setExpandidoDisparo(true);
      setSidebarAberto(true); // Abre sidebar ao navegar para aba com sub-abas
    } else if (abaId === 'financeiro') {
      if (abaAtiva === 'financeiro') {
        if (!sidebarAberto) {
          setSidebarAberto(true);
          setExpandidoFinanceiro(true);
        } else {
          setExpandidoFinanceiro(prev => !prev);
        }
        return;
      }
      setExpandidoFinanceiro(true);
      setSidebarAberto(true); // Abre sidebar ao navegar para aba com sub-abas
    } else {
      // Ao trocar para outra aba, recolhe os menus
      setExpandidoDisparo(false);
      setExpandidoFinanceiro(false);
    }

    setAbaAtiva(abaId);
    setMenuMobileAberto(false);
  };

  // ============================================
  // 🎨 TELA DE LOGIN
  // ============================================

  // MULTI-TENANT: subdomínio "admin" → renderiza painel super admin
  // (colocado aqui, depois de todos os hooks, para respeitar Rules of Hooks)
  if (isSuperAdmin) {
    return (
      <Suspense fallback={<TelaCarregando />}>
        <SuperAdminPage />
      </Suspense>
    );
  }

  if (carregandoAuth) {
    return <TelaCarregando />;
  }

  // Preview das landing pages (sem autenticação) — acesse via ?preview=landing
  if (new URLSearchParams(window.location.search).get('preview') === 'landing') {
    return (
      <Suspense fallback={<div style={{ background: '#060b14', minHeight: '100vh' }} />}>
        <LandingPreview />
      </Suspense>
    );
  }

  if (resetandoSenha) {
    return <ResetPasswordPage onConcluido={() => { setResetandoSenha(false); setUsuarioLogado(null); }} />;
  }

  // Rotas públicas (páginas legais e rastreio) — sem login
  const rotaPublica = renderRotaPublica();
  if (rotaPublica) {
    return rotaPublica;
  }

  if (!usuarioLogado) {
    return <LoginPage onLogin={handleLogin} erroInicial={extrairErroLogin()} />;
  }

  // ============================================
  // 🎨 CONTEÚDO DAS ABAS (compartilhado entre layouts)
  // ============================================
  const abaProps = { setTotalNaoLidas, clientesCache, subAbaDisparo, setSubAbaDisparo, subAbaFinanceiro, setSubAbaFinanceiro, usuarioLogado };
  const conteudoAbas = (
    <Suspense fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto mb-3"></div>
          <p className="text-slate-400 text-sm">Carregando...</p>
        </div>
      </div>
    }>
      {renderAbaAtiva(abaAtiva, abaProps)}
    </Suspense>
  );

  // Props de navegação compartilhadas entre os novos layouts
  const navProps = {
    abasVisiveis,
    abaAtiva,
    handleTrocarAba,
    subAbaDisparo,
    setSubAbaDisparo,
    subAbaFinanceiro,
    setSubAbaFinanceiro,
    expandidoDisparo,
    expandidoFinanceiro,
    SUB_ABAS_DISPARO,
    SUB_ABAS_FINANCEIRO,
    usuarioLogado,
    tenant,
    totalNaoLidas,
    atendenteAtivo,
    atendenteProcessando,
    handleToggleAtendente,
    handleLogout,
  };

  const modalBoasVindas = mostrarBoasVindas
    ? <ModalBoasVindas onFechar={() => setMostrarBoasVindas(false)} />
    : null;

  // ── Layout OrganizAI (novos clientes — marca 'organizai') ──────────────
  if (tenant?.marca === 'organizai') {
    return (
      <>
        <LayoutOrganizAI {...navProps}>{conteudoAbas}</LayoutOrganizAI>
        <AgenteSuporteChat />
        {modalBoasVindas}
      </>
    );
  }

  // ── Layout IntegrAI (novos clientes — marca 'integrai') ────────────────
  if (tenant?.marca === 'integrai') {
    return (
      <>
        <LayoutIntegrAI {...navProps}>{conteudoAbas}</LayoutIntegrAI>
        <AgenteSuporteChat />
        {modalBoasVindas}
      </>
    );
  }

  // ============================================
  // 🎨 RENDER PRINCIPAL (layout legado — clientes existentes, marca = null)
  // ============================================
  return (
    <>
      <AppLayout
        tenantId={tenant?.id}
        tenantNome={tenant?.nome}
        usuario={usuarioLogado.nome}
        perfil={usuarioLogado.perfil}
        onLogout={handleLogout}
        abaAtiva={abaAtiva}
        onNavegar={handleTrocarAba}
        modulos={calcularModulosOrdenados(tenant)}
        totalNaoLidas={totalNaoLidas}
        subAbaDisparoAtiva={subAbaDisparo}
        onSubAbaDisparo={setSubAbaDisparo}
        subAbaFinanceiroAtiva={subAbaFinanceiro}
        onSubAbaFinanceiro={setSubAbaFinanceiro}
        atendenteAtivo={atendenteAtivo}
        atendenteProcessando={atendenteProcessando}
        onToggleAtendente={handleToggleAtendente}
      >
        {conteudoAbas}
      </AppLayout>
      {modalBoasVindas}
      <Toaster position="top-right" richColors />
      <AgenteSuporteChat />
    </>
  );
};
export default CRMDashboard;
